import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send, RefreshCw, CheckCircle2 } from 'lucide-react';
import SEO from '../../components/SEO';
import { contactLeadAPI } from '../../api/apiService';

const ContactPage = () => {
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [countryCode, setCountryCode] = useState('+91');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        address: '',
        message: ''
    });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            // Map the frontend fields to the backend schema fields
            const fullPhone = `${countryCode} ${formData.phone}`;
            const payload = {
                name: formData.name,
                email: formData.email,
                phone: fullPhone,
                subject: `Contact Inquiry from ${formData.name}`,
                business_name: formData.company,
                message: `Company: ${formData.company || 'N/A'}\nAddress: ${formData.address || 'N/A'}\n\nMessage:\n${formData.message}`
            };

            await contactLeadAPI.submit(payload);
            setSubmitted(true);
        } catch (err) {
            console.error("Failed to submit contact lead:", err);
            alert("Something went wrong. Please try again later.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-[#022c22] min-h-screen text-white font-sans flex items-center justify-center relative overflow-hidden pt-28 pb-16">
            <SEO
                title="Contact Us"
                description="We'd love to hear from you! Let's get in touch to optimize your billing operations."
                url="/contact"
            />
            
            {/* Blurred background accents */}
            <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="max-w-6xl w-full mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
                
                {/* ⬅️ Left Section */}
                <div className="col-span-1 lg:col-span-5 space-y-8">
                    <div className="space-y-4">
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight">
                            Contact Us
                        </h1>
                        <p className="text-lg text-emerald-100/70 leading-relaxed font-light">
                            Not sure what you need? The team at Gio Billing will be happy to listen to you and suggest billing and inventory solutions you hadn't considered.
                        </p>
                    </div>

                    <div className="space-y-6 pt-4 border-t border-emerald-800/40">
                        <a 
                            href="mailto:support@giobilling.com" 
                            className="flex items-center gap-4 group p-2 -ml-2 rounded-lg hover:bg-emerald-900/30 transition-colors"
                        >
                            <div className="w-10 h-10 bg-emerald-900/50 border border-emerald-800/50 rounded-lg flex items-center justify-center text-emerald-400 group-hover:bg-emerald-500 group-hover:text-white transition-all">
                                <Mail size={18} />
                            </div>
                            <div>
                                <p className="text-xs text-emerald-100/50 font-bold uppercase tracking-wider">Email Us</p>
                                <p className="text-sm font-semibold group-hover:underline">support@giobilling.com</p>
                            </div>
                        </a>

                        <a 
                            href="tel:+919876543210" 
                            className="flex items-center gap-4 group p-2 -ml-2 rounded-lg hover:bg-emerald-900/30 transition-colors"
                        >
                            <div className="w-10 h-10 bg-emerald-900/50 border border-emerald-800/50 rounded-lg flex items-center justify-center text-emerald-400 group-hover:bg-emerald-500 group-hover:text-white transition-all">
                                <Phone size={18} />
                            </div>
                            <div>
                                <p className="text-xs text-emerald-100/50 font-bold uppercase tracking-wider">Call Us</p>
                                <p className="text-sm font-semibold group-hover:underline">Support: +91 98765 43210</p>
                            </div>
                        </a>

                        <div className="flex items-center gap-4 p-2 -ml-2">
                            <div className="w-10 h-10 bg-emerald-900/50 border border-emerald-800/50 rounded-lg flex items-center justify-center text-emerald-400">
                                <MapPin size={18} />
                            </div>
                            <div>
                                <p className="text-xs text-emerald-100/50 font-bold uppercase tracking-wider">Office Address</p>
                                <p className="text-sm font-semibold">KK Nagar, Chennai, Tamil Nadu, India.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ➡️ Right Section (Contact Form Card) */}
                <div className="col-span-1 lg:col-span-7 flex justify-center lg:justify-end">
                    <div className="bg-white text-gray-800 rounded-2xl shadow-2xl p-8 md:p-10 w-full max-w-xl border border-gray-100 relative">
                        {/* Decorative top-right graphic curve (matching the screenshot's outline details) */}
                        <div className="absolute top-0 right-0 w-24 h-24 overflow-hidden rounded-tr-2xl pointer-events-none opacity-20">
                            <div className="absolute top-[-50%] right-[-50%] w-32 h-32 border border-emerald-950 rounded-full" />
                            <div className="absolute top-[-40%] right-[-40%] w-32 h-32 border border-emerald-950 rounded-full" />
                            <div className="absolute top-[-30%] right-[-30%] w-32 h-32 border border-emerald-950 rounded-full" />
                        </div>

                        {submitted ? (
                            <div className="py-16 text-center space-y-4">
                                <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-sm">
                                    <CheckCircle2 size={36} />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900">Message Sent!</h3>
                                <p className="text-gray-500 max-w-sm mx-auto">
                                    Thank you for reaching out. A Gio Billing representative will be in touch with you shortly.
                                </p>
                                <button 
                                    onClick={() => setSubmitted(false)}
                                    className="mt-6 px-6 py-2.5 bg-[#022c22] hover:bg-[#064e3b] text-white text-sm font-semibold rounded-lg transition-colors"
                                >
                                    Send Another Message
                                </button>
                            </div>
                        ) : (
                            <div>
                                <div className="mb-8">
                                    <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest">
                                        We'd love to hear from you!
                                    </p>
                                    <h2 className="text-2xl font-bold text-gray-900 tracking-tight mt-1">
                                        Let's get in touch
                                    </h2>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-5">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label className="text-xs font-semibold text-gray-700 block">Full Name</label>
                                            <input 
                                                type="text" 
                                                name="name"
                                                placeholder="e.g. John Doe"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 outline-none focus:border-emerald-500 focus:bg-white transition-all text-sm text-gray-900"
                                                required
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-semibold text-gray-700 block">Company</label>
                                            <input 
                                                type="text" 
                                                name="company"
                                                placeholder="e.g. Acme Corp"
                                                value={formData.company}
                                                onChange={handleChange}
                                                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 outline-none focus:border-emerald-500 focus:bg-white transition-all text-sm text-gray-900"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label className="text-xs font-semibold text-gray-700 block">Email</label>
                                            <div className="relative">
                                                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                                <input 
                                                    type="email" 
                                                    name="email"
                                                    placeholder="olivia@untitledui.com"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-10 pr-4 py-2.5 outline-none focus:border-emerald-500 focus:bg-white transition-all text-sm text-gray-900"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-semibold text-gray-700 block">Phone number</label>
                                            <div className="flex rounded-lg overflow-hidden border border-gray-200 focus-within:border-emerald-500 transition-all bg-gray-50">
                                                <select 
                                                    value={countryCode} 
                                                    onChange={(e) => setCountryCode(e.target.value)}
                                                    className="bg-transparent border-r border-gray-200 text-sm px-2.5 py-2.5 outline-none text-gray-600 font-medium"
                                                >
                                                    <option value="+91">IN +91</option>
                                                    <option value="+1">US +1</option>
                                                    <option value="+44">UK +44</option>
                                                    <option value="+971">AE +971</option>
                                                </select>
                                                <input 
                                                    type="tel" 
                                                    name="phone"
                                                    placeholder="98765 43210"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    className="w-full bg-transparent px-3 py-2.5 outline-none text-sm text-gray-900 placeholder:text-gray-300"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-gray-700 block">Address</label>
                                        <input 
                                            type="text" 
                                            name="address"
                                            placeholder="Your business location details"
                                            value={formData.address}
                                            onChange={handleChange}
                                            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 outline-none focus:border-emerald-500 focus:bg-white transition-all text-sm text-gray-900"
                                        />
                                    </div>

                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-gray-700 block">Your Message</label>
                                        <textarea 
                                            name="message"
                                            placeholder="Type your message here..."
                                            value={formData.message}
                                            onChange={handleChange}
                                            rows={4}
                                            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 outline-none focus:border-emerald-500 focus:bg-white transition-all text-sm text-gray-900 resize-none"
                                            required
                                        />
                                    </div>

                                    <button 
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full bg-[#022c22] hover:bg-[#064e3b] text-white font-bold py-3.5 mt-2 transition-all active:scale-[0.98] text-sm rounded-lg shadow-lg shadow-emerald-950/10 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <RefreshCw className="animate-spin" size={16} />
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <Send size={16} />
                                                Send Message
                                            </>
                                        )}
                                    </button>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactPage;
